import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { clubAPI } from '../services/api';
import './ClubFund.css';

function ClubGoals() {
  const { clubId } = useParams();
  const [goals, setGoals] = useState([]);
  const [activeGoal, setActiveGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    targetAmount: '',
    amountPerPerson: '',
    deadline: '',
    targetType: 1,
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const loadGoals = useCallback(async () => {
    setLoading(true);
    try {
      const [goalsData, activeData] = await Promise.all([
        clubAPI.getGoals(clubId),
        clubAPI.getActiveGoal(clubId).catch(() => ({ goal: null })),
      ]);
      setGoals(goalsData.goals || []);
      setActiveGoal(activeData.goal || null);
    } catch (err) { /* ignore */ }
    setLoading(false);
  }, [clubId]);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setResult(null);
    try {
      await clubAPI.createGoal(clubId, {
        title: formData.title,
        description: formData.description || undefined,
        targetAmount: parseFloat(formData.targetAmount) || undefined,
        amountPerPerson: parseFloat(formData.amountPerPerson) || undefined,
        deadline: formData.deadline || undefined,
        targetType: formData.targetType,
      });
      setResult({ success: true, message: 'Goal created!' });
      setShowForm(false);
      setFormData({ title: '', description: '', targetAmount: '', amountPerPerson: '', deadline: '', targetType: 1 });
      loadGoals();
    } catch (err) {
      setResult({ success: false, message: err.response?.data?.error || 'Failed to create goal' });
    }
    setSaving(false);
  };

  const handleCancel = async (goalId) => {
    if (!window.confirm('Cancel this goal?')) return;
    try {
      await clubAPI.cancelGoal(goalId);
      loadGoals();
    } catch (err) { alert(err.response?.data?.error || 'Failed to cancel'); }
  };

  const handleAchieve = async (goalId) => {
    if (!window.confirm('Mark this goal as achieved? This will deactivate it.')) return;
    try {
      await clubAPI.achieveGoal(goalId);
      loadGoals();
    } catch (err) { alert(err.response?.data?.error || 'Failed — goal target may not be reached yet'); }
  };

  const fmtDollars = (cents) => '$' + (Number(cents || 0) / 100).toFixed(2);

  const hasActive = goals.some(g => g.isActive);

  return (
    <div>
      <h2>Club Goals</h2>

      {/* Active Goal Card with Progress */}
      {activeGoal && (
        <div style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.1), rgba(16,185,129,0.1))', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <div>
              <div style={{ color: '#fbbf24', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '0.25rem' }}>Active Goal</div>
              <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 700 }}>{activeGoal.title}</div>
            </div>
            {activeGoal.progress?.isReached && (
              <span style={{ background: '#065f46', color: '#10b981', fontSize: '0.7rem', fontWeight: 600, padding: '3px 10px', borderRadius: 20 }}>Target Reached!</span>
            )}
          </div>
          {activeGoal.description && <p style={{ color: '#999', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>{activeGoal.description}</p>}

          {/* Progress Bar */}
          {activeGoal.progress && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: '#ccc', marginBottom: '0.35rem' }}>
                <span>Balance: {fmtDollars(activeGoal.progress.balanceCents)}</span>
                <span>Target: {fmtDollars(activeGoal.progress.targetCents)}</span>
              </div>
              <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '6px', height: '10px', overflow: 'hidden', marginBottom: '0.5rem' }}>
                <div style={{
                  width: `${Math.min(activeGoal.progress.progressPercent, 100)}%`,
                  height: '100%',
                  borderRadius: '6px',
                  background: activeGoal.progress.isReached
                    ? 'linear-gradient(90deg, #10b981, #34d399)'
                    : 'linear-gradient(90deg, #fbbf24, #f59e0b)',
                  transition: 'width 0.5s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#666' }}>
                <span>{activeGoal.progress.progressPercent}%</span>
                <span>{activeGoal.progress.isReached ? 'Goal reached!' : `${fmtDollars(activeGoal.progress.remainingCents)} remaining`}</span>
              </div>
            </div>
          )}

          {activeGoal.hasDeadline && activeGoal.deadline && (
            <div style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.5rem' }}>
              Deadline: {new Date(activeGoal.deadline).toLocaleDateString()}
            </div>
          )}

          {/* Captain Actions */}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            {activeGoal.progress?.isReached && (
              <button onClick={() => handleAchieve(activeGoal.id)} style={{ flex: 1, background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                Mark as Achieved
              </button>
            )}
            <button onClick={() => handleCancel(activeGoal.id)} style={{ flex: activeGoal.progress?.isReached ? 0.5 : 1, background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '6px', padding: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' }}>
              Cancel Goal
            </button>
          </div>
        </div>
      )}

      {/* Create Button */}
      {!showForm && !hasActive && (
        <button onClick={() => setShowForm(true)} className="btn-submit" style={{ marginBottom: '1.5rem', width: '100%' }}>
          + Set a New Goal
        </button>
      )}

      {hasActive && !showForm && (
        <p style={{ color: '#666', fontSize: '0.8rem', marginBottom: '1rem', fontStyle: 'italic' }}>
          Only one active goal at a time. Cancel or achieve the current goal to set a new one.
        </p>
      )}

      {/* Create Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="fund-form">
          <h3 style={{ color: '#fff', marginBottom: '1rem' }}>New Goal</h3>

          <div className="form-group">
            <label>Goal Title *</label>
            <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required placeholder="e.g., Melbourne Cup Trip"
              style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', padding: '0.75rem', boxSizing: 'border-box' }} />
          </div>

          <div className="form-group">
            <label>Description</label>
            <input type="text" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="What's the goal about?"
              style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', padding: '0.75rem', boxSizing: 'border-box' }} />
          </div>

          <div className="form-group">
            <label>Target Type</label>
            <select value={formData.targetType} onChange={e => setFormData({...formData, targetType: parseInt(e.target.value)})}
              style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', padding: '0.75rem', boxSizing: 'border-box' }}>
              <option value={1}>Total Amount (fixed target)</option>
              <option value={2}>Per Person (target scales with members)</option>
            </select>
          </div>

          {formData.targetType === 1 ? (
            <div className="form-group">
              <label>Target Amount ($)</label>
              <div className="amount-input-group">
                <span className="currency-symbol">$</span>
                <input type="number" value={formData.targetAmount} onChange={e => setFormData({...formData, targetAmount: e.target.value})} min="1" step="1" required placeholder="500" />
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label>Amount Per Person ($)</label>
              <div className="amount-input-group">
                <span className="currency-symbol">$</span>
                <input type="number" value={formData.amountPerPerson} onChange={e => setFormData({...formData, amountPerPerson: e.target.value})} min="1" step="1" required placeholder="100" />
              </div>
              <small style={{ color: '#666', fontSize: '0.75rem' }}>Total target = this amount x number of active members</small>
            </div>
          )}

          <div className="form-group">
            <label>Deadline (optional)</label>
            <input type="date" value={formData.deadline} onChange={e => setFormData({...formData, deadline: e.target.value})}
              style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', padding: '0.75rem', boxSizing: 'border-box' }} />
          </div>

          {result && <div className={`result-message ${result.success ? 'success' : 'error'}`}>{result.message}</div>}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" className="btn-submit" disabled={saving} style={{ flex: 1 }}>
              {saving ? 'Creating...' : 'Create Goal'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setResult(null); }}
              style={{ flex: '0 0 auto', background: 'rgba(255,255,255,0.05)', color: '#999', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '0.85rem 1.5rem', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Past Goals */}
      <div className="recent-transactions">
        <h3>Goal History</h3>
        {loading ? <p style={{ color: '#666' }}>Loading...</p> :
          goals.filter(g => !g.isActive).length === 0 ? <p className="no-transactions">No past goals</p> :
          goals.filter(g => !g.isActive).map((g) => (
            <div key={g.id} style={{ background: 'rgba(0,0,0,0.25)', borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#ccc', fontWeight: 700 }}>{g.title}</span>
                <span style={{
                  fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: g.isAchieved ? '#065f46' : g.isCancelled ? '#7f1d1d' : '#1c1917',
                  color: g.isAchieved ? '#10b981' : g.isCancelled ? '#ef4444' : '#666',
                }}>{g.isAchieved ? 'Achieved' : g.isCancelled ? 'Cancelled' : 'Inactive'}</span>
              </div>
              {g.description && <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.25rem 0' }}>{g.description}</p>}
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#555', marginTop: '0.25rem' }}>
                <span>Target: {fmtDollars(g.targetAmountCents)}</span>
                <span>{new Date(g.created).toLocaleDateString()}</span>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

export default ClubGoals;
